import { Checkbox } from '@vibe/core';
import { useTranslation } from '@/i18n/useTranslation';
import type { DesignationTrafficLight, MapLegendEntry } from '@/lib/api/map';
import { useMapDesignationFilter } from '@/context/MapDesignationFilterContext';
import styles from './MapDesignationFilterList.module.css';

function trafficLightClass(light: DesignationTrafficLight | undefined): string {
  switch (light) {
    case 'green':
      return styles.ramzorGreen;
    case 'yellow':
      return styles.ramzorYellow;
    case 'red':
      return styles.ramzorRed;
    default:
      return styles.ramzorUnknown;
  }
}

type Props = {
  entries: MapLegendEntry[];
};

export default function MapDesignationFilterList({ entries }: Props) {
  const t = useTranslation();
  const { enabledCodes, toggleCode } = useMapDesignationFilter();

  if (entries.length === 0) {
    return (
      <p className={styles.empty} data-testid="map-designation-filter-empty">
        {t.map.legend.filterEmpty}
      </p>
    );
  }

  return (
    <ul className={styles.list} data-testid="map-designation-filter-list">
      {entries.map((entry) => {
        const checked = enabledCodes.has(entry.code);
        return (
          <li key={entry.code} className={styles.item}>
            {entry.traffic_light ? (
              <span
                className={`${styles.ramzorDot} ${trafficLightClass(entry.traffic_light)}`}
                aria-hidden
              />
            ) : null}
            <span
              className={entry.pattern === 'hatch' ? styles.swatchHatch : styles.swatch}
              style={{ backgroundColor: entry.color }}
              aria-hidden
            />
            <Checkbox
              checked={checked}
              onChange={(e) => {
                if (e.target.checked !== checked) {
                  toggleCode(entry.code);
                }
              }}
              label={entry.label}
              aria-label={entry.label}
              data-testid={`map-designation-${entry.code}`}
            />
          </li>
        );
      })}
    </ul>
  );
}
